import { FC, useState } from 'react';
import { Paper, Group, Text, Stack, Button } from '@mantine/core';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import {
  IconId,
  IconCake,
  IconGenderMale,
  IconGenderFemale,
  IconMapPin,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IDENTIFIERS } from '@consts/systems.consts';
import { calculateAge } from '@utils/calculate-age';
import dayjs from 'dayjs';
import { ViewButton } from '@/components/ViewButton';
import { EditButton } from '@/components/EditButton';
import { PatientViewDemographics } from '@/pages/ainigma/patient/demographics-actions/PatientViewDemographics';
import { PatientEditDemographicsModal } from '@/pages/ainigma/patient/demographics-actions/PatientEditDemographicsModal';
import { PatientNotesDrawer } from '@/pages/ainigma/patient/patient-notes/PatientNotesDrawer';

interface PatientInfoBannerProps {
  patient: Patient;
  onPatientUpdate?: (updatedPatient: Patient) => void;
  showDemographicsButton?: boolean;
  onOpenDemographics?: () => void;
  currentEncounter?: Encounter;
  minimizedView?: boolean;
}

export const PatientInfoBanner: FC<PatientInfoBannerProps> = ({
  patient,
  onPatientUpdate,
  showDemographicsButton,
  onOpenDemographics,
  currentEncounter,
  minimizedView = false,
}) => {
  const { t } = useTranslation(['patient-pages', 'encounters', 'common']);

  // Modal states
  const [viewModalOpened, setViewModalOpened] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [notesDrawerOpened, setNotesDrawerOpened] = useState(false);

  // Extract patient data
  const fullName = formatHumanName(patient.name?.[0]);
  const amka = patient?.identifier?.find(
    (id) => id.system === IDENTIFIERS.AMKA
  )?.value;
  const age = calculateAge(patient?.birthDate);
  const birthDate = patient.birthDate
    ? dayjs(patient.birthDate).format('DD/MM/YYYY')
    : null;
  const gender = patient.gender
    ? t(`summaryPage.summary.gender.${patient.gender}` as any)
    : null;
  const city = patient.address?.[0]?.city ?? '-';

  // Gender icon
  const GenderIcon =
    patient.gender === 'female'
      ? IconGenderFemale
      : patient.gender === 'male'
        ? IconGenderMale
        : null;

  return (
    <Paper p="md">
      <Stack gap="xs">
        {/* Patient Name */}
        <Group>
          <Text size="lg" fw={600}>
            {fullName}
          </Text>
          <ViewButton
            onClick={() => {
              setViewModalOpened(true);
            }}
          />
          <EditButton
            onClick={() => {
              setEditModalOpened(true);
            }}
          />
          <Button
            size="xs"
            variant="light"
            onClick={() => setNotesDrawerOpened(true)}
          >
            {t('notes.title', {
              ns: 'patient-pages',
              defaultValue: 'Patient Notes',
            })}
          </Button>
          {showDemographicsButton && (
            <Button
              variant="light"
              size="xs"
              onClick={onOpenDemographics}
              ml="auto"
            >
              {currentEncounter
                ? t('inProgress', {
                    ns: 'encounters',
                  })
                : t('summaryPage.summary.patientProfile.title', {
                    ns: 'patient-pages',
                  })}
            </Button>
          )}
        </Group>

        {/* Patient Demographics & Current Encounter - Hidden in minimized view */}
        {!minimizedView && (
          <Group gap="lg" align="center">
            {/* AMKA */}
            {amka && (
              <Group gap="xs">
                <IconId size={16} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">
                  <Text component="span" size="sm" fw={500} c="dark">
                    {t('banner.amka', { ns: 'patient-pages' })}:
                  </Text>{' '}
                  {amka}
                </Text>
              </Group>
            )}

            {/* Date of Birth and Age */}
            {birthDate && (
              <Group gap="xs">
                <IconCake size={16} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">
                  <Text component="span" size="sm" fw={500} c="dark">
                    {t('banner.dateOfBirth', { ns: 'patient-pages' })}:
                  </Text>{' '}
                  {birthDate}
                  {age !== null && (
                    <>
                      {' '}
                      ({age} {t('banner.years', { ns: 'patient-pages' })})
                    </>
                  )}
                </Text>
              </Group>
            )}

            {/* City */}
            {city && (
              <Group gap="xs">
                <IconMapPin size={16} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">
                  <Text component="span" size="sm" fw={500} c="dark">
                    {t('banner.city', {
                      ns: 'patient-pages',
                      defaultValue: 'City',
                    })}
                    :
                  </Text>{' '}
                  {city}
                </Text>
              </Group>
            )}

            {/* Gender */}
            {gender && GenderIcon && (
              <Group gap="xs">
                <GenderIcon size={16} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">
                  {gender}
                </Text>
              </Group>
            )}
          </Group>
        )}
      </Stack>

      {/* View Demographics Modal */}
      <PatientViewDemographics
        opened={viewModalOpened}
        onClose={() => setViewModalOpened(false)}
        patient={patient}
      />

      {/* Edit Demographics Modal */}
      <PatientEditDemographicsModal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        patient={patient}
        onPatientSaved={(updatedPatient) => {
          onPatientUpdate?.(updatedPatient);
        }}
      />

      {/* Patient Notes Drawer */}
      <PatientNotesDrawer
        opened={notesDrawerOpened}
        onClose={() => setNotesDrawerOpened(false)}
      />
    </Paper>
  );
};
